import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useEditTrapModalStore } from './EditTrapModalStore';

import { Hub, Trap } from 'api/traps/types';
import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import { TrapTableType } from 'components/TrapsTables/trapTables';
import useGetAddMultiTrapsData from 'hooks/Traps/useGetAddMultiTrapsData';
import useGetTrap from 'hooks/Traps/Trap/useGetTrap';
import useUpdateTrap from 'hooks/Traps/useUpdateTrap';

interface EditTrapModalProps {
  refetchTraps: () => void;
}

const EditTrapModal: FC<EditTrapModalProps> = ({ refetchTraps }) => {
  const { isOpen, onEditTrapModalClose, slug, trap, trapType } = useEditTrapModalStore();
  const [selectedTrap, setSelectedTrap] = useState<string | number | null>(null);
  const { trapOptions } = useGetAddMultiTrapsData(slug);
  const { updateTrap } = useUpdateTrap();
  const { trapInfo } = useGetTrap(slug, trap?.pk || 0);
  const { t } = useTranslation();
  const currentHub: { value: string; label: string } | undefined = trapOptions?.hubs
    .map(hub => ({
      value: String(hub.pk),
      label: hub.serial_number,
    }))
    .find(hub => hub.value === String(trapInfo?.hub));

  const { handleSubmit, control, reset } = useForm<Trap>({
    defaultValues: {
      sequence_number: 0,
      recomended_bait_filling: 0,
      hub: 0,
    },
  });

  useEffect(() => {
    if (trapInfo) {
      reset(trapInfo);
    }
  }, [reset, trapInfo]);

  const onReset = () => {
    reset();
    onEditTrapModalClose();
  };

  const onTrapEdit = (data: Trap) => {
    updateTrap(
      {
        trapId: trap?.pk || 0,
        slug,
        trapInfo: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Trap updated successfully', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchTraps();
        },
        onError: () => {
          enqueueSnackbar('Trap did`t update', {
            variant: 'error',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={onReset} size="md">
        <ModalHeader toggle={onReset} className="border-bottom pb-3 pt-3 ">
          {trapType === TrapTableType.SmartRodenBaitModule
            ? t('TrapModals.manageModal.editStation')
            : t('TrapModals.manageModal.editTrap')}

          {` ${trap?.serialNumber}`}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="row">
              <div className="mb-3 col-md-12">
                <MLSelect
                  label={
                    trapType === TrapTableType.SmartRodenBaitModule
                      ? t('TrapModals.manageModal.station')
                      : t('TrapModals.manageModal.trap')
                  }
                  value={selectedTrap}
                  onChange={setSelectedTrap}
                  options={[{ value: `${trap?.pk}`, label: `${trap?.serialNumber}` }]}
                />
              </div>
              <div className="mb-3 col-md-12">
                <Controller
                  control={control}
                  name="sequence_number"
                  rules={{
                    required: 'Required field',
                    min: { value: 1, message: 'Sequence number must be positive' },
                  }}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      {...field}
                      label={t('TrapModals.manageModal.sequenceNumber')}
                      error={errors?.sequence_number?.message}
                      required
                      type="number"
                      onChange={e => field.onChange(+e.target.value)}
                    />
                  )}
                />
              </div>
            </div>
            {trapType &&
            [
              TrapTableType.MultiCatchMouseTraps,
              TrapTableType.SmartRodenBaitModule,
              TrapTableType.SnapTrapRat,
              TrapTableType.SnapTrapMouse,
            ].includes(trapType) ? (
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Controller
                    name="hub"
                    control={control}
                    rules={{ required: 'Required field' }}
                    render={({ field, formState: { errors } }) => (
                      <MLSelect
                        {...field}
                        label={t('TrapModals.manageModal.hub')}
                        required
                        error={errors?.hub?.message}
                        onChange={selectedOption => {
                          field.onChange(selectedOption);
                        }}
                        {...(!trapOptions?.hubs || trapOptions?.hubs.length === 0
                          ? {
                              disabled: true,
                              options: [{ value: '', label: 'No Hubs' }],
                            }
                          : {
                              options: [currentHub || { value: '0', label: 'None' }].concat(
                                trapOptions?.hubs
                                  ?.filter((hub: Hub) => hub.pk !== trapInfo?.hub)
                                  .map(hub => ({
                                    value: hub.pk.toString(),
                                    label: hub.serial_number,
                                  })),
                              ),
                            })}
                      />
                    )}
                  />
                </div>
              </div>
            ) : null}
            {trapType === TrapTableType.SmartRodenBaitModule || trapType === TrapTableType.BaitStations ? (
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Controller
                    name="recomended_bait_filling"
                    control={control}
                    rules={{
                      required: 'Required field',
                      min: { value: 1, message: 'Recommended bait filling must be positive' },
                    }}
                    render={({ field, formState: { errors } }) => (
                      <MLInput
                        {...field}
                        required
                        label={t('TrapModals.manageModal.recommendedBaitFilling')}
                        error={errors?.recomended_bait_filling?.message}
                        type="number"
                      />
                    )}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="w-100 d-flex justify-content-end">
            <Button
              className="btn btn-ghost-danger"
              onClick={() => {
                onReset();
              }}
            >
              {t('Modals.delete-modal.cancel')}
            </Button>

            <Button className="btn btn-ghost-success" onClick={handleSubmit(onTrapEdit)}>
              {t('TrapModals.confirm')}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EditTrapModal;
